import React, { useState, useEffect } from 'react';
import './ModelSelector.css';
import { getModelsConfig } from '../../../services/agentcore.service';

const ModelSelector = ({ value, onChange, disabled = false }) => {
  const [models, setModels] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInfo, setShowInfo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load models configuration
    const loadModels = async () => {
      try {
        setLoading(true);
        const data = await getModelsConfig();
        setModels(data.models || []);
        setCategories(data.categories || {});
        
        // Set default if no value provided
        if (!value && data.default_model) {
          onChange(data.default_model);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to load models config:', err);
        setError('Failed to load models');
        // Fallback to default
        if (!value) {
          onChange('us.meta.llama3-3-70b-instruct-v1:0');
        }
      } finally {
        setLoading(false);
      }
    };

    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const filteredModels = selectedCategory === 'all' 
    ? models 
    : models.filter(m => m.category === selectedCategory);

  const groupedModels = filteredModels.reduce((acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  }, {});

  const selectedModel = models.find(m => m.id === value);

  // Category emoji mapping
  const categoryEmojis = {
    'premium': '🌟',
    'standard': '⚡',
    'budget': '💰'
  };

  if (loading) {
    return (
      <div className="model-selection loading">
        <div className="model-header">
          <label className="model-label">🤖 Loading Models...</label>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="model-selection error">
        <div className="model-header">
          <label className="model-label">
            ⚠️ Error loading models: {error}
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="model-selection">
      <div className="model-header">
        <label className="model-label">
          🤖 Select AI Model:
          {selectedModel && (
            <span className="selected-model-info">
              {selectedModel.name}
            </span>
          )}
        </label>
        <button 
          type="button"
          className="info-btn"
          onClick={() => setShowInfo(!showInfo)}
          title="Model information"
        >
          i
        </button>
      </div>

      {showInfo && (
        <div className="model-info-banner">
          <p>
            <strong>💡 Choosing the right model:</strong><br />
            <span className="info-tier">🌟 Premium:</span> Best for complex analysis, highest quality<br />
            <span className="info-tier">⚡ Standard:</span> Good balance of quality and cost<br />
            <span className="info-tier">💰 Budget:</span> Fast and economical for simple tasks
          </p>
        </div>
      )}

      {/* Category Filter */}
      <div className="category-filter">
        <button
          type="button"
          className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('all')}
          disabled={disabled}
        >
          All Models
        </button>
        {Object.entries(categories).map(([key, cat]) => (
          <button
            key={key}
            type="button"
            className={`filter-btn ${selectedCategory === key ? 'active' : ''}`}
            onClick={() => setSelectedCategory(key)}
            disabled={disabled}
          >
            {categoryEmojis[key]} {cat.label}
          </button>
        ))}
      </div>

      {/* Model Selection */}
      <div className="model-options">
        {Object.entries(groupedModels).map(([provider, providerModels]) => (
          <div key={provider} className="model-provider-group">
            <h4 className="provider-name">{provider}</h4>
            {providerModels.map(model => (
              <label 
                key={model.id} 
                className={`model-option ${value === model.id ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  value={model.id}
                  checked={value === model.id}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={disabled}
                />
                <div className="model-details">
                  <div className="model-name-row">
                    <span className="model-name">{model.name}</span>
                    <span className={`model-badge ${model.category}`}>
                      {categoryEmojis[model.category]} {categories[model.category]?.label || model.category}
                    </span>
                  </div>
                  <div className="model-description">{model.description}</div>
                  <div className="model-specs">
                    <span className="spec">📄 {model.contextWindow}</span>
                    <span className={`spec cost-${model.costTier}`}>
                      💲 {model.costTier}
                    </span>
                    {model.type === 'inference_profile' && (
                      <span className="spec profile-badge">⚡ Inference Profile</span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        ))}
      </div>

      {selectedModel && (
        <div className="selected-model-summary">
          <strong>Selected:</strong> {selectedModel.name} 
          <span className="model-id-display">({selectedModel.id})</span>
        </div>
      )}
    </div>
  );
};

export default ModelSelector;
